import { getDaysInMonth } from 'date-fns';
import { DateTimeSDK } from './dates';

/** A recurring yearly window, stored as two `month * 100 + day` integers.
 *
 * Used by featured activity categories so seasonal selections ("Winter specials",
 * "Lente specials") light up in the header every year without anyone having to
 * flip a checkbox. The year is deliberately not stored: the window repeats. */
export namespace SeasonalWindow {
    /** `month * 100 + day`, e.g. 1201 = 1 December, 228 = 28 February. */
    export type MonthDay = number;

    export type Window = {
        from: MonthDay | null;
        until: MonthDay | null;
    };

    /** Month lengths are read off a leap year, so February always offers 29 days:
     * a window has no year, and 29 February must stay pickable in any year. */
    const LEAP_YEAR = 2024;

    export function daysInMonth(month: number): number {
        if (month < 1 || month > 12) return 31;
        return getDaysInMonth(new Date(LEAP_YEAR, month - 1));
    }

    /** Packs a month and day into the stored integer, e.g. (12, 1) → 1201. */
    export function encode(month: number, day: number): MonthDay {
        return month * 100 + day;
    }

    /** Unpacks a stored integer back into its month and day, e.g. 1201 → 1 December. */
    export function decode(monthDay: MonthDay): { month: number; day: number } {
        return { month: Math.floor(monthDay / 100), day: monthDay % 100 };
    }

    /** A window ignores the year, so 29 February is always a legal day. */
    export function isValid(monthDay: MonthDay): boolean {
        if (!Number.isInteger(monthDay)) return false;

        const { month, day } = decode(monthDay);
        if (month < 1 || month > 12) return false;

        return day >= 1 && day <= daysInMonth(month);
    }

    /** Both ends must be set together — a half-open window has no meaning here. */
    export function isComplete(window: Window): boolean {
        return (window.from === null) === (window.until === null);
    }

    /** Whether `window` covers `on` (defaults to today in Brussels).
     *
     * An unset window is always active, which keeps categories that predate this
     * feature featured year-round. Both ends are inclusive, and a window whose
     * `from` is after its `until` wraps across new year (1 Dec → 28 Feb). */
    export function isActive(window: Window, on: Date = DateTimeSDK.bruDate()): boolean {
        if (window.from === null || window.until === null) return true;

        const date = DateTimeSDK.ISODate.decode(DateTimeSDK.bruDate(on));
        const today = encode(date.month, date.day);

        if (window.from <= window.until) return today >= window.from && today <= window.until;
        return today >= window.from || today <= window.until;
    }
}
